import type { CompanyModel } from "models/CompanyModel";
import type { UserModel } from "models/UserModel";

import { compiledClientConfig } from "config";
import { getCurrentVersion } from "utils/version";

let gainsightInitialized = false;

export type TLoginDetails = {
  method: "oidc" | "saml";
  provider: "google" | "microsoft" | "okta";
};

export const gainsightInit = async () => {
  const clientId = compiledClientConfig.gainsightClientId;
  if (clientId) {
    if (typeof window.aptrinsic !== "function") return;

    window.aptrinsic("init", clientId, {});
    gainsightInitialized = true;

    // Send custom version/env event
    const version = await getCurrentVersion();
    const environment = compiledClientConfig.appEnv;
    const region = compiledClientConfig.appRegion;

    window.aptrinsic("track", "App Loaded", { environment, region, version });
    gainsightInitialized = true;
  }
};

export const setGainsightUserScope = (
  user: UserModel,
  company: CompanyModel,
  loginDetails?: { method: "oidc" | "saml"; provider: "google" | "microsoft" | "okta" },
) => {
  if (typeof window.aptrinsic !== "function" || !user) return;
  const loginMethod = loginDetails ? `${loginDetails.method} (${loginDetails.provider})` : undefined;

  window.aptrinsic(
    "identify",
    {
      email: user.email,
      firstName: user.givenName,
      id: user.id,
      isAdmin: user.isAdmin,
      lastName: user.familyName,
      loginMethod,
      region: compiledClientConfig.appRegion,
      role: user.role,
      signUpDate: new Date().toISOString(),
    },
    { id: company.id, name: company.name },
  );

  if (loginMethod) {
    gainsightLog("User Login", { loginMethod });
  }
};

export const gainsightLog = (message: string, extra?: Record<string, unknown>) => {
  if (!gainsightInitialized) return;
  window.aptrinsic("track", message, { ...extra, source: "frontend" });
};

export const gainsightLogError = (error: Error, extra?: Record<string, unknown>) => {
  if (!gainsightInitialized) return;
  window.aptrinsic("track", "Client Error", {
    message: error.message,
    stack: error.stack,
    ...extra,
    source: "frontend",
  });
};
